(function () {
  "use strict";

  const QB_VERSION = "0.9.1";

  const SLOT_MODULES = ["rewards", "wishlist", "promos", "account", "orders", "chat", "faq"];

  // Base slot config. Per-client overrides go in window.QB.slotsConfig
  // (theme setting or headless app config) — merged per slot at init, so
  // clients never need to edit this file.
  const QB_SLOTS = {
    rewards: {
      enabled: true,
      label: "Rewards",
      icon: "star",
    },
    wishlist: {
      enabled: true,
      label: "Saved",
      icon: "heart",
    },
    promos: {
      enabled: true,
      label: "Deals",
      icon: "tag",
      teaser: true, // trigger teaser chip for the first matching promo
      autoOpen: { enabled: false, delaySeconds: 8 }, // popup-style, off by default
    },
    chat: {
      enabled: true,
      label: "Chat",
      icon: "chat",
      provider: "gorgias",
      embedScript: "",
    },
    faq: {
      enabled: true,
      label: "Help",
      icon: "question",
      source: "static",
      items: [
        // Offline fallback only — the live answers are qb_faq metaobjects,
        // which support per-market targeting. Amounts stay currency-qualified
        // here because this copy is served to every market unchanged.
        { q: "How do I earn points?", a: "Earn 10 points for every NZ$1 of order value on full-price items, 3 on reduced. Orders in another currency are converted to NZD first." },
        { q: "How do I redeem points?", a: "Redeem 1,000 points for a NZ$10 reward in the Rewards tab. It lands as store credit in your own currency — sign in at checkout to use it." },
      ],
    },
    account: {
      enabled: true,
      label: "Account",
      icon: "person",
    },
    orders: {
      enabled: true,
      label: "Orders",
      icon: "box",
    },
  };

  // window.QB.theme keys → CSS custom properties. Anything not listed here
  // can still be set via theme.vars = { "--qb-whatever": "value" }.
  const THEME_VAR_MAP = {
    accent: "--qb-accent",
    accentText: "--qb-accent-text",
    bg: "--qb-bg",
    surface: "--qb-surface",
    text: "--qb-text",
    textMuted: "--qb-text-muted",
    border: "--qb-border",
    radius: "--qb-radius",
    btnRadius: "--qb-btn-radius",
    font: "--qb-font",
    drawerWidth: "--qb-drawer-width",
    drawerBorder: "--qb-drawer-border",
    tapeHeight: "--qb-tape-height",
    shadow: "--qb-shadow",
    triggerSize: "--qb-trigger-size",
    triggerOffsetX: "--qb-trigger-offset-x",
    triggerOffsetY: "--qb-trigger-offset-y",
    cardBg: "--qb-card-bg",
    cardText: "--qb-card-text",
    logoHeight: "--qb-logo-height",
    progressTrack: "--qb-progress-track",
    error: "--qb-error",
    errorBg: "--qb-error-bg",
  };

  const THEME_PRESETS = ["default", "soft", "pill", "midnight"];

  // Engagement telemetry. The ledger already measures the money side exactly;
  // nothing measured whether anyone opens the drawer, so "which tab do people
  // use" and "does opening it precede a purchase" had no data behind them.
  //
  // Fire-and-forget on purpose, in every sense: keepalive so a click that
  // navigates away still sends, no await anywhere, and every failure swallowed.
  // A shopper must never experience an analytics problem. The endpoint
  // allowlists the event and slot names, so nothing here can write free text.
  function track(event, opts = {}) {
    try {
      const workerUrl = window.QB?.worker?.url;
      if (!workerUrl) return;
      const token = window.QB?.authToken;
      let deviceToken;
      try {
        deviceToken = localStorage.getItem("qb_device_token") || undefined;
      } catch {
        /* private mode — an anonymous row is still worth having */
      }
      fetch(`${workerUrl}/track`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ event, slot: opts.slot ?? null, meta: opts.meta ?? null, token: deviceToken }),
      }).catch(() => {});
    } catch {
      /* never let telemetry surface to a shopper */
    }
  }

  window.QB = window.QB || {};
  window.QB.track = track;

  // Merchant-configurable copy. Priority: Worker /config `messages` (KV →
  // qb_hub_settings metaobject) → window.QB.messagesConfig (theme JSON) →
  // the fallback baked into each call site. Slots read via QB.msg(key, fb).
  function msg(key, fallback) {
    const server = window.QB?.messages;
    if (server && typeof server[key] === "string" && server[key]) return server[key];
    const local = window.QB?.messagesConfig;
    if (local && typeof local[key] === "string" && local[key]) return local[key];
    return fallback;
  }

  // One /config fetch per page view (the endpoint is public and cached for
  // 5 minutes, so this is cheap). QB.messagesReady lets slots that render
  // copy wait for it without racing.
  function loadMessages() {
    window.QB.messages = window.QB.messages || {};
    const url = window.QB?.worker?.url;
    if (!url) return Promise.resolve();
    return fetch(`${url}/config`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.messages && typeof data.messages === "object") {
          window.QB.messages = data.messages;
        }
        // Merchant-edited help articles (qb_faq metaobjects / KV via the
        // Worker). The FAQ slot prefers these over its static `items`.
        if (data && Array.isArray(data.faq)) {
          window.QB.serverFaq = data.faq;
        }
      })
      .catch(() => {});
  }

  // Login/register URLs with a return_to that lands the shopper back on this
  // page with the drawer open on `slot` (?qb-open=<slot> deep link). On new
  // customer accounts the hosted login honours return_to and also creates the
  // storefront session, so the round trip comes back signed in; on classic
  // stores the param is ignored harmlessly. Relative return_to only — hosted
  // login rejects foreign absolute URLs as open redirects.
  function accountUrl(base, slot) {
    if (!slot) return base;
    const params = new URLSearchParams(window.location.search);
    params.set("qb-open", slot);
    const returnTo = window.location.pathname + "?" + params.toString();
    return base + (base.indexOf("?") === -1 ? "?" : "&") + "return_to=" + encodeURIComponent(returnTo);
  }

  function loginUrl(slot) {
    return accountUrl(window.QB?.urls?.login || "/account/login", slot);
  }

  function registerUrl(slot) {
    return accountUrl(window.QB?.urls?.register || "/account/register", slot);
  }

  function isSafeImageUrl(url) {
    if (typeof url !== "string" || !url) return false;
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === "https:" || parsed.protocol === "http:";
    } catch {
      return false;
    }
  }

  const ICONS = {
    star: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    heart: '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>',
    chat: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
    question: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    person: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
    box: '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>',
    tag: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
    close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    menu: '<line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>',
  };

  function svgIcon(name) {
    const path = ICONS[name] || ICONS.menu;
    return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">${path}</svg>`;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function getSlotConfig() {
    const overrides = window.QB?.slotsConfig || {};
    const merged = {};
    for (const key of Object.keys(QB_SLOTS)) {
      merged[key] = { ...QB_SLOTS[key], ...(overrides[key] || {}) };
    }
    return merged;
  }

  function getEnabledSlots() {
    return Object.entries(getSlotConfig())
      .filter(([, config]) => config.enabled)
      .map(([key, config]) => ({ key, ...config }));
  }

  // Apply window.QB.theme to the drawer root. Precedence (lowest → highest):
  // stylesheet defaults → data-qb-preset block → inline vars set here.
  function applyTheme(root) {
    const theme = window.QB?.theme;
    if (!root || !theme || typeof theme !== "object") return;

    if (theme.preset && THEME_PRESETS.includes(theme.preset)) {
      root.dataset.qbPreset = theme.preset;
    }

    if (theme.position === "left") {
      root.classList.add("qb-drawer--left");
    } else if (theme.position === "right") {
      root.classList.remove("qb-drawer--left");
    }

    for (const [key, cssVar] of Object.entries(THEME_VAR_MAP)) {
      if (theme[key] !== undefined && theme[key] !== null && theme[key] !== "") {
        root.style.setProperty(cssVar, String(theme[key]));
      }
    }

    if (theme.vars && typeof theme.vars === "object") {
      for (const [name, value] of Object.entries(theme.vars)) {
        if (name.startsWith("--qb-")) root.style.setProperty(name, String(value));
      }
    }
  }

  // Brand treatment (docs/THEMING.md — Branding). All optional:
  //   theme.logo        — image URL shown in the drawer header
  //   theme.logoOnly    — true hides the title text next to the logo
  //   theme.headerStyle — "accent" paints the header in the accent colour
  //   theme.trigger     — "logo" renders the logo in the trigger button
  //   theme.brandName   — logo alt text
  //   theme.tape        — repeating image strip across the very top of the panel
  //   theme.tapeHeight  — tape strip height (defaults to 28px once tape is set)
  //   theme.tapeScroll  — true animates the tape as a slow seamless marquee
  //   theme.tapeSpeed   — marquee speed in px/s (default 24)
  function applyBranding(root) {
    const theme = window.QB?.theme;
    if (!root || !theme || typeof theme !== "object") return;

    if (theme.headerStyle === "accent") {
      root.dataset.qbHeader = "accent";
    }

    if (isSafeImageUrl(theme.tape)) {
      root.style.setProperty("--qb-tape", `url("${theme.tape.replace(/"/g, "%22")}")`);
      if (!theme.tapeHeight) root.style.setProperty("--qb-tape-height", "28px");

      // Marquee needs the rendered tile width (image ratio × strip height) so
      // each animation loop lands exactly one tile over — that's what makes
      // the repeat seamless. Measure the image, then enable the animation.
      if (theme.tapeScroll) {
        const img = new Image();
        img.onload = () => {
          if (!img.naturalHeight) return;
          const height = parseFloat(theme.tapeHeight) || 28;
          const tile = (img.naturalWidth / img.naturalHeight) * height;
          const speed = parseFloat(theme.tapeSpeed) > 0 ? parseFloat(theme.tapeSpeed) : 24;
          root.style.setProperty("--qb-tape-shift", `${tile.toFixed(2)}px`);
          root.style.setProperty("--qb-tape-duration", `${(tile / speed).toFixed(2)}s`);
          root.dataset.qbTapeScroll = "true";
        };
        img.src = theme.tape;
      }
    }

    if (!isSafeImageUrl(theme.logo)) return;
    const header = root.querySelector(".qb-drawer__header");
    const title = header?.querySelector(".qb-drawer__title");
    if (header && title && !header.querySelector(".qb-drawer__brand")) {
      const brand = document.createElement("div");
      brand.className = "qb-drawer__brand";
      const img = document.createElement("img");
      img.className = "qb-drawer__logo";
      img.src = theme.logo;
      img.alt = theme.brandName || "";
      header.insertBefore(brand, title);
      brand.appendChild(img);
      brand.appendChild(title);
      if (theme.logoOnly) root.classList.add("qb-drawer--logo-only");
    }

    if (theme.trigger === "logo") {
      const trigger = root.querySelector(".qb-drawer-trigger");
      if (trigger) {
        trigger.innerHTML = `<img class="qb-drawer-trigger__logo" src="${escapeHtml(theme.logo)}" alt="" />`;
        trigger.classList.add("qb-drawer-trigger--logo");
      }
    }
  }

  // Headless storefronts don't have the Liquid snippet, so the drawer can
  // build its own markup: call QB.initDrawer() and the root + shell are
  // created if missing. window.QB.customer must be populated first.
  function buildShellHtml() {
    const customer = window.QB?.customer;
    const title = customer?.loggedIn && customer.firstName
      ? `Hi ${escapeHtml(customer.firstName)}`
      : "Customer Hub";

    return `
      <button
        class="qb-drawer-trigger"
        type="button"
        aria-label="Open customer hub"
        aria-expanded="false"
        aria-controls="qb-drawer-panel"
      >${svgIcon("menu")}</button>
      <div class="qb-drawer-overlay" aria-hidden="true"></div>
      <aside
        id="qb-drawer-panel"
        class="qb-drawer"
        role="dialog"
        aria-label="Customer hub"
        aria-modal="true"
      >
        <header class="qb-drawer__header">
          <h2 class="qb-drawer__title">${title}</h2>
          <button class="qb-drawer__close" type="button" aria-label="Close drawer">${svgIcon("close")}</button>
        </header>
        <nav class="qb-drawer__tabs" role="tablist" aria-label="Customer hub sections"></nav>
        <div class="qb-drawer__content"></div>
      </aside>
    `;
  }

  const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class QBDrawer {
    constructor(options = {}) {
      this.isOpen = false;
      this.activeTab = null;
      this.slots = getEnabledSlots();
      this.slotInstances = {};

      this.root = document.getElementById("qb-drawer-root");
      if (!this.root) {
        // No root in the page. On Liquid installs the snippet provides it, so
        // auto-init just bails. An explicit QB.initDrawer() call (headless)
        // creates the root itself.
        if (options.auto) return;
        this.root = document.createElement("div");
        this.root.id = "qb-drawer-root";
        document.body.appendChild(this.root);
      }
      if (!this.root.querySelector(".qb-drawer")) {
        this.root.innerHTML = buildShellHtml();
      }

      applyTheme(this.root);
      applyBranding(this.root);

      this.trigger = this.root.querySelector(".qb-drawer-trigger");
      this.overlay = this.root.querySelector(".qb-drawer-overlay");
      this.drawer = this.root.querySelector(".qb-drawer");
      this.tabsEl = this.root.querySelector(".qb-drawer__tabs");
      this.contentEl = this.root.querySelector(".qb-drawer__content");
      this.closeBtn = this.root.querySelector(".qb-drawer__close");

      this.renderTabs();
      this.renderPanels();
      this.bindTabOverflow();
      this.initSlotModules();
      this.bindEvents();

      if (this.slots.length > 0) {
        this.setActiveTab(this.slots[0].key);
      }

      // Deep link: #qb-open or #qb-open=<slot> opens the drawer on load, so
      // site banners / nav links can point straight at a panel. ?qb-open=<slot>
      // does the same — query params survive auth redirects (the login
      // round-trip return_to), where hash fragments often don't.
      const hashMatch = window.location.hash.match(/^#qb-open(?:=([a-z]+))?$/);
      const queryOpen = new URLSearchParams(window.location.search).get("qb-open");
      if (hashMatch) {
        this.open(hashMatch[1]);
      } else if (queryOpen !== null) {
        this.open(/^[a-z]+$/.test(queryOpen) ? queryOpen : undefined);
      }

      // Copy overrides: theme-JSON ones apply now, server ones when fetched.
      this.applyMessages();
      window.QB?.messagesReady?.then(() => this.applyMessages());
    }

    // Header title from merchant copy — only overrides when a key is set, so
    // the Liquid/built-in defaults stay untouched otherwise.
    applyMessages() {
      const titleEl = this.root?.querySelector(".qb-drawer__title");
      if (!titleEl) return;
      const customer = window.QB?.customer;
      if (customer?.loggedIn && customer.firstName) {
        const greeting = msg("drawer_greeting", "");
        if (greeting) titleEl.textContent = greeting.replace("{firstName}", customer.firstName);
      } else {
        const title = msg("drawer_title", "");
        if (title) titleEl.textContent = title;
      }
    }

    // Small dismissible chip anchored to the trigger — the low-noise
    // replacement for a signup popup's discoverability. Shown until clicked
    // or dismissed (per session); slots supply the text (e.g. promos teaser).
    showTeaser(text, tabKey) {
      if (!this.root || !this.trigger || this.isOpen || !text) return;
      if (this.root.querySelector(".qb-drawer-teaser")) return;
      try {
        if (sessionStorage.getItem("qbTeaserDismissed")) return;
      } catch {
        /* no storage — still show it */
      }

      const teaser = document.createElement("div");
      teaser.className = "qb-drawer-teaser";
      teaser.innerHTML = `
        <button class="qb-drawer-teaser__text" type="button">${escapeHtml(text)}</button>
        <button class="qb-drawer-teaser__close" type="button" aria-label="Dismiss">${svgIcon("close")}</button>
      `;

      const dismiss = () => {
        teaser.remove();
        try {
          sessionStorage.setItem("qbTeaserDismissed", "1");
        } catch {
          /* fine */
        }
      };

      teaser.querySelector(".qb-drawer-teaser__text").addEventListener("click", () => {
        dismiss();
        this.open(tabKey);
      });
      teaser.querySelector(".qb-drawer-teaser__close").addEventListener("click", dismiss);

      this.root.appendChild(teaser);
    }

    renderTabs() {
      if (!this.tabsEl) return;

      this.tabsEl.innerHTML = this.slots
        .map(
          (slot) => `
          <button
            class="qb-drawer__tab"
            data-tab="${slot.key}"
            type="button"
            aria-selected="false"
          >
            <span class="qb-drawer__tab-icon">${svgIcon(slot.icon)}</span>
            <span>${escapeHtml(slot.label)}</span>
          </button>
        `
        )
        .join("");
    }

    renderPanels() {
      if (!this.contentEl) return;

      this.contentEl.innerHTML = this.slots
        .map((slot) => {
          if (SLOT_MODULES.includes(slot.key)) {
            return `<div class="qb-drawer__panel" data-panel="${slot.key}" role="tabpanel"></div>`;
          }
          return `
          <div class="qb-drawer__panel" data-panel="${slot.key}" role="tabpanel">
            <div class="qb-drawer__panel-placeholder">
              ${escapeHtml(slot.label)} slot — coming soon
            </div>
          </div>`;
        })
        .join("");
    }

    initSlotModules() {
      this.contentEl?.querySelectorAll(".qb-drawer__panel").forEach((panel) => {
        const key = panel.dataset.panel;
        const module = window.QB?.slotModules?.[key];
        if (module?.init && !panel.dataset.slotInit) {
          panel.dataset.slotInit = "true";
          this.slotInstances[key] = module.init(panel);
        }
      });
    }

    bindEvents() {
      this.trigger?.addEventListener("click", () => this.open());
      this.closeBtn?.addEventListener("click", () => this.close());
      this.overlay?.addEventListener("click", () => this.close());

      this.tabsEl?.addEventListener("click", (e) => {
        const tab = e.target.closest(".qb-drawer__tab");
        if (tab) this.setActiveTab(tab.dataset.tab);
      });

      document.addEventListener("keydown", (e) => {
        if (!this.isOpen) return;
        if (e.key === "Escape") {
          this.close();
          return;
        }
        if (e.key === "Tab") this.trapFocus(e);
      });
    }

    // Keep Tab cycling inside the open drawer (aria-modal requirement).
    trapFocus(e) {
      const focusables = this.drawer?.querySelectorAll(FOCUSABLE);
      if (!focusables?.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!this.drawer.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    setActiveTab(key) {
      // Only while open: this also runs during render to select the default
      // tab, and a view nobody saw is not a view.
      if (this.isOpen && key !== this.activeTab) track("slot_view", { slot: key });
      this.activeTab = key;

      this.tabsEl?.querySelectorAll(".qb-drawer__tab").forEach((tab) => {
        const isActive = tab.dataset.tab === key;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        // A deep link (?qb-open=orders) can select a tab that is scrolled off
        // screen on a phone, which reads as "the drawer opened on nothing".
        if (isActive) this.revealTab(tab);
      });

      this.contentEl?.querySelectorAll(".qb-drawer__panel").forEach((panel) => {
        panel.classList.toggle("is-active", panel.dataset.panel === key);
      });

      const instance = this.slotInstances[key];
      if (instance?.onActivate) instance.onActivate();
    }

    // Scroll the active tab into the strip without scrolling the PAGE —
    // scrollIntoView() on a nested overflow container will happily move the
    // whole document on some browsers, which yanks the drawer around.
    bindTabOverflow() {
      const strip = this.tabsEl;
      if (!strip) return;
      strip.addEventListener("scroll", () => this.updateTabOverflow(), { passive: true });
      // Recompute when the drawer opens (clientWidth is 0 while hidden, so a
      // measurement taken at init would always say "no overflow") and on
      // resize / orientation change.
      window.addEventListener("resize", () => this.updateTabOverflow());
      if (typeof ResizeObserver === "function") {
        new ResizeObserver(() => this.updateTabOverflow()).observe(strip);
      }
      this.updateTabOverflow();
    }

    revealTab(tab) {
      const strip = this.tabsEl;
      if (!strip || !tab) return;
      const left = tab.offsetLeft;
      const right = left + tab.offsetWidth;
      const viewLeft = strip.scrollLeft;
      const viewRight = viewLeft + strip.clientWidth;
      if (left < viewLeft) {
        strip.scrollTo({ left: Math.max(0, left - 12), behavior: "smooth" });
      } else if (right > viewRight) {
        strip.scrollTo({ left: right - strip.clientWidth + 12, behavior: "smooth" });
      }
      this.updateTabOverflow();
    }

    // Fade only the edge that is actually clipped. With the scrollbar hidden
    // there is otherwise no cue at all that the strip scrolls.
    updateTabOverflow() {
      const strip = this.tabsEl;
      if (!strip) return;
      const max = strip.scrollWidth - strip.clientWidth;
      if (max <= 1) return strip.setAttribute("data-overflow", "none");
      const atStart = strip.scrollLeft <= 1;
      const atEnd = strip.scrollLeft >= max - 1;
      strip.setAttribute(
        "data-overflow",
        atStart ? "right" : atEnd ? "left" : "both"
      );
    }

    open(tab) {
      // Only the transition counts. open() is also called to switch tabs on an
      // already-open drawer, and counting those would inflate opens by exactly
      // the customers who use it most.
      const wasOpen = this.isOpen;
      this.isOpen = true;
      if (!wasOpen) track("open", { slot: tab && this.slots.some((s) => s.key === tab) ? tab : null });
      this.drawer?.classList.add("is-open");
      this.overlay?.classList.add("is-open");
      this.trigger?.setAttribute("aria-expanded", "true");
      document.body.classList.add("qb-drawer-open");
      this.root?.querySelector(".qb-drawer-teaser")?.remove();
      if (tab && this.slots.some((s) => s.key === tab)) {
        this.setActiveTab(tab);
      }
      // Measure once visible: clientWidth is 0 while the drawer is hidden, so
      // any earlier reading reports "nothing to scroll" and the fade never
      // appears. rAF so it runs after the open transition has laid out.
      requestAnimationFrame(() => {
        this.updateTabOverflow();
        const active = this.tabsEl?.querySelector(".qb-drawer__tab.is-active");
        if (active) this.revealTab(active);
      });
      this.closeBtn?.focus();
    }

    close() {
      if (this.isOpen) track("close", { slot: this.activeTab ?? null });
      this.isOpen = false;
      this.drawer?.classList.remove("is-open");
      this.overlay?.classList.remove("is-open");
      this.trigger?.setAttribute("aria-expanded", "false");
      document.body.classList.remove("qb-drawer-open");
      this.trigger?.focus();
    }
  }

  function init(options = {}) {
    window.QB = window.QB || {};
    window.QB.version = QB_VERSION;
    window.QB.msg = msg;
    window.QB.loginUrl = loginUrl;
    window.QB.registerUrl = registerUrl;
    if (!window.QB.messagesReady) window.QB.messagesReady = loadMessages();
    window.QB.drawer = new QBDrawer(options);
    window.QB.slots = getSlotConfig();
    window.QB.applyTheme = () => applyTheme(window.QB.drawer?.root);
  }

  window.QB = window.QB || {};
  window.QB.initDrawer = () => init();

  const autoInit = () => init({ auto: true });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
})();
